import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
// @mui
import { Box } from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
//
import { COMPANY } from '../../config/company';
import { CONTAINER, FONT, STAGE, TYPE } from '../../theme/stage';
import { LEGAL, LegalDoc } from '../home/copy';
import { CSS_EASE } from '../home/motion';
import Eyebrow from '../home/primitives/Eyebrow';
import Rich from '../home/primitives/Rich';

// ----------------------------------------------------------------------

type Props = {
  doc: LegalDoc;
};

const TITLE_ID = 'legal-title';

const TOC_ID = 'legal-toc';

const WIDE = '@media (min-width: 1100px)';

const PRINT = '@media print';

const HOVER = '@media (hover: hover)';

const rule = `1px solid ${STAGE.line}`;

const BODY = { fontFamily: FONT.sans, fontWeight: 400, fontSize: 17, lineHeight: '28px' };

const MEASURE = '50ch';

function useHashTarget() {
  const { pathname, search, hash, key } = useLocation();

  const navigationType = useNavigationType();

  const navigate = useNavigate();

  useEffect(() => {
    const target = hash && document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target || (navigationType === 'POP' && key !== 'default')) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      target.scrollIntoView();
      if (key === 'default') {
        navigate(`${pathname}${search}${hash}`, { replace: true });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, search, hash, key, navigationType, navigate]);
}

function Contents({ doc }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Box
      component="nav"
      aria-label={LEGAL.tocLabel}
      sx={{
        minWidth: 0,
        [WIDE]: {
          position: 'sticky',
          top: 96,
          alignSelf: 'start',
          maxHeight: 'calc(100vh - 128px)',
          overflowY: 'auto',
          m: '-6px',
          p: '6px',
        },
        [PRINT]: { display: 'none' },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-controls={TOC_ID}
        onClick={() => setOpen(!open)}
        sx={{
          ...TYPE.label,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          width: '100%',
          minHeight: 48,
          px: '16px',
          border: rule,
          borderRadius: open ? '12px 12px 0 0' : '12px',
          bgcolor: 'transparent',
          color: STAGE.ink2,
          cursor: 'pointer',
          [HOVER]: { '&:hover': { bgcolor: STAGE.surface } },
          [WIDE]: { display: 'none' },
        }}
      >
        {LEGAL.tocLabel}
        <ExpandMoreRounded
          aria-hidden
          sx={{
            fontSize: 20,
            transform: open ? 'rotate(180deg)' : 'none',
            '@media (prefers-reduced-motion: no-preference)': {
              transition: `transform 200ms ${CSS_EASE.press}`,
            },
          }}
        />
      </Box>

      <Box
        component="p"
        aria-hidden
        sx={{
          ...TYPE.label,
          display: 'none',
          m: '0 0 12px',
          color: STAGE.mute,
          [WIDE]: { display: 'block' },
        }}
      >
        {LEGAL.tocLabel}
      </Box>

      <Box
        component="ol"
        id={TOC_ID}
        sx={{
          display: open ? 'block' : 'none',
          listStyle: 'none',
          m: 0,
          p: '4px 16px 8px',
          border: rule,
          borderTop: 0,
          borderRadius: '0 0 12px 12px',
          [WIDE]: { display: 'block', p: 0, border: 0, borderTop: rule, borderRadius: 0 },
        }}
      >
        {doc.sections.map((section, index) => (
          <Box key={section.id} component="li" sx={{ [WIDE]: { borderBottom: rule } }}>
            <Box
              component="a"
              href={`#${section.id}`}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '12px',
                minHeight: 44,
                py: '12px',
                borderRadius: '6px',
                fontFamily: FONT.sans,
                fontSize: 15,
                lineHeight: '20px',
                color: STAGE.ink2,
                textDecoration: 'none',
                [HOVER]: { '&:hover': { color: STAGE.ink } },
              }}
            >
              <Box
                component="span"
                sx={{
                  ...TYPE.label,
                  flexShrink: 0,
                  width: 20,
                  fontVariantNumeric: 'tabular-nums',
                  color: STAGE.mute,
                }}
              >
                {index + 1}
              </Box>{' '}
              {section.title}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function LegalLayout({ doc }: Props) {
  useHashTarget();

  return (
    <Box
      component="article"
      aria-labelledby={TITLE_ID}
      sx={{
        ...CONTAINER,
        pt: { xs: '104px', sm: '144px' },
        pb: { xs: '96px', sm: '128px' },
        [PRINT]: {
          p: 0,
          '&, & *': {
            color: '#000000 !important',
            borderColor: '#8A8A8A !important',
            bgcolor: 'transparent !important',
          },
          '& a[href^="http"]::after': { content: '" (" attr(href) ")"' },
        },
      }}
    >
      <Box component="header" sx={{ pb: { xs: '32px', sm: '40px' }, borderBottom: rule }}>
        <Eyebrow>{LEGAL.eyebrow}</Eyebrow>
        <Box
          component="h1"
          id={TITLE_ID}
          sx={{ ...TYPE.heroTitle, fontSize: { xs: 40, sm: 52, lg: 64 }, m: 0, color: STAGE.ink }}
        >
          {doc.title}
        </Box>
        <Box component="p" sx={{ ...TYPE.fine, m: '16px 0 0', fontSize: 13, color: STAGE.mute }}>
          {LEGAL.updated(COMPANY.policiesUpdated)}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(0, ${MEASURE})`,
          rowGap: '32px',
          fontFamily: BODY.fontFamily,
          fontSize: BODY.fontSize,
          mt: '32px',
          [WIDE]: { gridTemplateColumns: '240px minmax(0, 1fr)', columnGap: '64px', mt: '56px' },
          [PRINT]: { display: 'block', mt: '24px' },
        }}
      >
        <Contents doc={doc} />

        <Box sx={{ ...BODY, minWidth: 0, maxWidth: MEASURE, color: STAGE.ink2 }}>
          <Box
            component="section"
            sx={{
              p: { xs: '24px', sm: '28px 32px' },
              border: rule,
              borderRadius: '16px',
              bgcolor: STAGE.surface,
            }}
          >
            <Box component="h2" sx={{ ...TYPE.label, m: '0 0 14px', color: STAGE.mute }}>
              {LEGAL.summaryLabel}
            </Box>
            <Box
              component="ul"
              sx={{ display: 'grid', rowGap: '10px', listStyle: 'none', m: 0, p: 0 }}
            >
              {doc.summary.map((point) => (
                <Box
                  key={point}
                  component="li"
                  sx={{
                    position: 'relative',
                    pl: '20px',
                    lineHeight: '26px',
                    color: STAGE.ink,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '10px',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: STAGE.bisque,
                    },
                    [PRINT]: { '&::before': { bgcolor: '#000000' } },
                  }}
                >
                  {point}
                </Box>
              ))}
            </Box>
          </Box>

          {doc.sections.map((section, index) => (
            <Box
              key={section.id}
              component="section"
              sx={{ mt: '48px', [PRINT]: { mt: '28px', breakInside: 'avoid' } }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '4px',
                  '&:hover .LegalLayout-anchor': { opacity: 1 },
                }}
              >
                <Box
                  component="h2"
                  id={section.id}
                  sx={{
                    m: 0,
                    fontFamily: FONT.sans,
                    fontWeight: 500,
                    fontSize: { xs: 22, sm: 24 },
                    lineHeight: { xs: '28px', sm: '30px' },
                    letterSpacing: '-0.015em',
                    color: STAGE.ink,
                    textWrap: 'balance',
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: STAGE.mute, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {`${index + 1}.`}
                  </Box>{' '}
                  {section.title}
                </Box>
                <Box
                  component="a"
                  className="LegalLayout-anchor"
                  href={`#${section.id}`}
                  aria-label={LEGAL.sectionLinkLabel}
                  aria-describedby={section.id}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: 44,
                    height: 44,
                    mt: { xs: '-8px', sm: '-7px' },
                    borderRadius: '8px',
                    fontFamily: FONT.mono,
                    fontSize: 20,
                    lineHeight: 1,
                    color: STAGE.mute,
                    textDecoration: 'none',
                    opacity: 0,
                    '&:focus-visible': { opacity: 1 },
                    '@media (hover: none)': { opacity: 1 },
                    [HOVER]: { '&:hover': { color: STAGE.ink } },
                    [PRINT]: { display: 'none' },
                  }}
                >
                  #
                </Box>
              </Box>
              <Box component="p" sx={{ m: '14px 0 0', textWrap: 'pretty' }}>
                <Rich parts={section.body} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
